import { db } from '@/firebase/config'
import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore'
import { getAllMissions } from '@/utils/jsnMissions'

/**
 * Schedule all missions for a child based on account creation date
 * @param {string} childId - Child user ID
 * @param {Date} accountCreatedDate - The date when child account was created
 * @param {string} parentId - Parent user ID for ownership tracking
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const scheduleChildMissions = async (
  childId,
  accountCreatedDate,
  parentId
) => {
  try {
    if (!childId) {
      throw new Error('Child ID is required')
    }

    if (!accountCreatedDate) {
      throw new Error('Account created date is required')
    }

    if (!parentId) {
      throw new Error('Parent ID is required')
    }

    // Check if schedule already exists
    const scheduleRef = doc(db, 'childMissionSchedule', childId)
    const scheduleDoc = await getDoc(scheduleRef)

    if (scheduleDoc.exists()) {
      // Schedule already exists, don't recreate
      return { success: true, alreadyExists: true }
    }

    // Get all missions
    const allMissions = getAllMissions()

    // Ensure accountCreatedDate is a Date object
    const startDate =
      accountCreatedDate instanceof Date
        ? accountCreatedDate
        : new Date(accountCreatedDate)

    // Calculate due dates for each mission
    // First mission: account creation date
    // Subsequent missions: one month after previous mission
    const scheduledMissions = allMissions.map((mission, index) => {
      const dueDate = new Date(startDate)

      // Add months based on mission index (0 = first mission, 1 = second mission, etc.)
      if (index > 0) {
        dueDate.setMonth(dueDate.getMonth() + index)
      }

      return {
        missionId: mission.id,
        missionName: mission.title,
        dueDate: dueDate,
        canChildPlay: true
      }
    })

    // Create the schedule document
    const scheduleData = {
      role: 'CHILD',
      childId: childId,
      parentId: parentId,
      scheduledMissions: scheduledMissions,
      accountCreatedDate: startDate,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    }

    await setDoc(scheduleRef, scheduleData)

    return { success: true, alreadyExists: false }
  } catch (error) {
    console.error('Error scheduling child missions:', error)
    return { success: false, error: error.message }
  }
}

/**
 * Get child mission schedule
 * @param {string} childId - Child user ID
 * @returns {Promise<Object|null>} - Schedule data or null if not found
 */
export const getChildMissionSchedule = async (childId) => {
  try {
    if (!childId) {
      throw new Error('Child ID is required')
    }

    const scheduleRef = doc(db, 'childMissionSchedule', childId)
    const scheduleDoc = await getDoc(scheduleRef)

    if (!scheduleDoc.exists()) {
      return null
    }

    const data = scheduleDoc.data()
    return {
      id: scheduleDoc.id,
      ...data,
      accountCreatedDate:
        data.accountCreatedDate?.toDate?.() || data.accountCreatedDate,
      scheduledMissions:
        data.scheduledMissions?.map((mission) => ({
          ...mission,
          dueDate: mission.dueDate?.toDate?.() || new Date(mission.dueDate)
        })) || []
    }
  } catch (error) {
    console.error('Error getting child mission schedule:', error)
    throw error
  }
}

/**
 * Ensure each child has a mission schedule; create one if missing.
 * Runs in background - does not throw. Call once on parent dashboard load.
 * @param {string} parentId - Parent user ID
 * @param {Array<{childId: string, accountCreated?: string|Date}>} children - Children profiles
 */
export const ensureChildMissionSchedulesIfNeeded = async (
  parentId,
  children
) => {
  if (!parentId || !children?.length) return
  for (const child of children) {
    const childId = child?.childId
    if (!childId) continue
    try {
      const existing = await getChildMissionSchedule(childId)
      if (existing) continue
      const accountCreated =
        child.accountCreated != null
          ? new Date(child.accountCreated)
          : new Date()
      await scheduleChildMissions(childId, accountCreated, parentId)
    } catch {
      // Ignore per-child errors; continue with others
    }
  }
}

/**
 * Update canChildPlay status for a specific mission
 * @param {string} childId - Child user ID
 * @param {number} missionId - Mission ID to update
 * @param {boolean} canChildPlay - Whether child can play this mission
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const updateMissionAccess = async (childId, missionId, canChildPlay) => {
  try {
    if (!childId) {
      throw new Error('Child ID is required')
    }

    if (!missionId) {
      throw new Error('Mission ID is required')
    }

    const scheduleRef = doc(db, 'childMissionSchedule', childId)
    const scheduleDoc = await getDoc(scheduleRef)

    if (!scheduleDoc.exists()) {
      throw new Error('Child mission schedule not found')
    }

    const data = scheduleDoc.data()
    const scheduledMissions = data.scheduledMissions || []

    // Find and update the specific mission
    const updatedMissions = scheduledMissions.map((mission) => {
      if (mission.missionId === missionId) {
        return {
          ...mission,
          canChildPlay: canChildPlay
        }
      }
      return mission
    })

    // Update the document
    await updateDoc(scheduleRef, {
      scheduledMissions: updatedMissions,
      updatedAt: serverTimestamp()
    })

    return { success: true }
  } catch (error) {
    console.error('Error updating mission access:', error)
    return { success: false, error: error.message }
  }
}
